/**
 * Trunnion AI - Global Keyboard Shortcuts Hook
 * Registers keydown listeners with modifier key detection and scope management.
 */

import { useEffect, useRef } from 'react';

export interface HotkeyConfig {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  enabled?: boolean;
  /** Prevent firing when user is typing in an input/textarea */
  ignoreInputs?: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

function matchesHotkey(event: KeyboardEvent, config: HotkeyConfig): boolean {
  const wantsMod = config.meta || config.ctrl;
  const modPressed = isMac ? event.metaKey : event.ctrlKey;

  if (wantsMod && !modPressed) return false;
  if (!wantsMod && (event.metaKey || event.ctrlKey)) return false;
  if ((config.shift ?? false) !== event.shiftKey) return false;
  if ((config.alt ?? false) !== event.altKey) return false;

  return event.key.toLowerCase() === config.key.toLowerCase();
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

/**
 * Register a single keyboard shortcut.
 */
export function useHotkey(config: HotkeyConfig) {
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const c = configRef.current;
      if (c.enabled === false) return;
      if ((c.ignoreInputs !== false) && isInputFocused()) return;
      if (!matchesHotkey(e, c)) return;

      e.preventDefault();
      e.stopPropagation();
      c.action();
    }

    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, []);
}

/**
 * Register multiple keyboard shortcuts at once.
 */
export function useHotkeys(configs: HotkeyConfig[]) {
  const configsRef = useRef(configs);
  configsRef.current = configs;

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      for (const c of configsRef.current) {
        if (c.enabled === false) continue;
        if ((c.ignoreInputs !== false) && isInputFocused()) continue;
        if (!matchesHotkey(e, c)) continue;

        e.preventDefault();
        e.stopPropagation();
        c.action();
        return;
      }
    }

    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, []);
}

/**
 * Returns a human-readable label for a shortcut (e.g., "Cmd+K" on Mac, "Ctrl+K" on Windows).
 */
export function formatShortcut(config: Pick<HotkeyConfig, 'key' | 'ctrl' | 'meta' | 'shift' | 'alt'>): string {
  const parts: string[] = [];
  if (config.meta || config.ctrl) parts.push(isMac ? '\u2318' : 'Ctrl');
  if (config.shift) parts.push(isMac ? '\u21E7' : 'Shift');
  if (config.alt) parts.push(isMac ? '\u2325' : 'Alt');
  parts.push(config.key.length === 1 ? config.key.toUpperCase() : config.key);
  return parts.join(isMac ? '' : '+');
}
